import type { Ref } from 'vue';
import type { BookedJob } from '@/helpers/tables/bookedJobs';

import { onUnmounted, ref } from 'vue';
import { isCancel } from 'axios';

import { useUserStore } from '@/stores/useUserStore';
import { fetchGetJobsBookedByUser } from '@/helpers/fetchers';

export interface BookedJobsListFetchComposable {
  isFetching: Ref<boolean>;
  items: Ref<BookedJob[] | null>;
  total: Ref<number | undefined>;
  isError: Ref<boolean>;
  fetchBookedJobs: (page: number, pageSize: number) => Promise<void>;
  cancel: () => void;
}

export default function useBookedJobsListFetch(): BookedJobsListFetchComposable {
  const userStore = useUserStore();

  let controller: AbortController | undefined;
  const isFetching = ref(false);
  const items = ref<BookedJob[] | null>(null);
  const total = ref<number | undefined>(undefined);
  const isError = ref(false);

  async function fetchBookedJobs(
    page: number,
    pageSize: number,
  ): Promise<void> {
    if (controller) controller.abort();
    const thisController = new AbortController();
    controller = thisController;
    isFetching.value = true;
    isError.value = false;
    await fetchGetJobsBookedByUser(
      userStore.user.timezone,
      { page, pageSize },
      { signal: thisController.signal },
    )
      .then((data) => {
        items.value = data.results;
        total.value = data.count;
      })
      .catch((err: unknown) => {
        if (isCancel(err)) return undefined;
        items.value = null;
        total.value = undefined;
        isError.value = true;
        throw err;
      })
      .finally(() => {
        if (controller === thisController) isFetching.value = false;
      });
  }

  function cancel(): void {
    if (controller) controller.abort();
  }

  onUnmounted(cancel);

  return { isFetching, items, total, isError, fetchBookedJobs, cancel };
}
